export interface InitData {
  user?: {
    id: number
    first_name: string
    last_name?: string
    username?: string
    language_code?: string
    is_premium?: boolean
    avatar_color?: string
    /** URL аватарки из MAX (см. dev.max.ru WebApp initData). */
    photo_url?: string
  }
  auth_date: number
  hash: string
  start_param?: string
}

export interface Bridge {
  getInitData(): InitData
  getInitDataRaw(): string
  requestContact(): Promise<{ phone: string }>
  haptic(type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'selection'): void
  backButton: {
    show(): void
    hide(): void
    onClick(cb: () => void): void
    offClick(cb: () => void): void
  }
  openMaxLink(url: string): void
  openLink(url: string): void
  share(params: { text?: string; link?: string }): Promise<'shared' | 'cancelled'>
  closingConfirmation: {
    enable(): void
    disable(): void
  }
  platform: 'ios' | 'android' | 'desktop' | 'web'
  version: string
}

declare global {
  interface Window {
    WebApp?: {
      initData?: string
      initDataUnsafe?: InitData
      platform?: string
      version?: string
      ready(): void
      /** Официальный MAX: `Promise<{ phone: string }>`; старые клиенты — колбэк с `phone_number`. */
      requestContact?:
        | (() => Promise<{ phone?: string; phone_number?: string } | void>)
        | ((cb: (contact: { phone_number?: string; phone?: string } | null | undefined) => void) => void)
      hapticFeedback?(type: string): void
      openLink(url: string): void
      openInvoice(url: string): void
      close(): void
      showPopup(params: any, callback?: (id: string) => void): void
      setBackButtonCallback(callback: () => void): void
      offBackButtonPressed(callback: () => void): void
      showBackButton(): void
      hideBackButton(): void
      onEvent(eventType: string, eventHandler: (data: any) => void): void
      offEvent(eventType: string, eventHandler: (data: any) => void): void
      shareToStory(url: string, callback?: (result: boolean) => void): void
      enableClosingConfirmation(): void
      disableClosingConfirmation(): void
    }
  }
}

function getWebApp() {
  try {
    if (typeof window === 'undefined') {
      console.warn('[Bridge] window is not defined')
      return null
    }

    if (import.meta.env.DEV) {
      console.log('[Bridge] Checking window.WebApp:', {
        available: !!window.WebApp,
        hasInitData: !!(window.WebApp?.initData),
        hasInitDataUnsafe: !!(window.WebApp?.initDataUnsafe),
        platform: window.WebApp?.platform,
        version: window.WebApp?.version,
        webappKeys: window.WebApp ? Object.keys(window.WebApp) : [],
      })
    }

    if (!window.WebApp) {
      console.warn('[Bridge] window.WebApp is not available. Open this mini-app in MAX client.')
      return null
    }

    return window.WebApp
  } catch (err) {
    console.warn('[Bridge] Error accessing WebApp:', err)
    return null
  }
}

export const bridge: Bridge = {
  getInitData(): InitData {
    const webapp = getWebApp()
    if (!webapp || !webapp.initDataUnsafe) {
      console.warn('initData not available, using fallback')
      return {
        user: {
          id: 0,
          first_name: 'Guest',
          username: 'guest',
        },
        auth_date: Math.floor(Date.now() / 1000),
        hash: 'dev-hash',
      }
    }
    return webapp.initDataUnsafe
  },

  getInitDataRaw(): string {
    const webapp = getWebApp()
    if (!webapp || !webapp.initData) {
      console.warn('[Bridge] initData not available, using fallback', {
        webappAvailable: !!webapp,
        webappInitData: webapp?.initData,
        type: typeof webapp?.initData,
      })
      return 'dev-init-data'
    }
    if (import.meta.env.DEV) {
      console.log('[Bridge] getInitDataRaw ok, length:', webapp.initData.length)
    }
    return webapp.initData
  },

  async requestContact(): Promise<{ phone: string }> {
    const webapp = getWebApp()
    if (!webapp?.requestContact) {
      throw new Error('requestContact not available')
    }

    const fn = webapp.requestContact.bind(webapp) as (...args: unknown[]) => unknown

    /** Документация dev.max.ru: `requestContact()` → `Promise<{ phone: string }>`. */
    let first: unknown
    try {
      first = fn()
    } catch {
      first = undefined
    }

    if (first != null && typeof (first as Promise<unknown>).then === 'function') {
      const timeoutMs = 120_000
      const data = await Promise.race([
        first as Promise<{ phone?: string; phone_number?: string } | null | undefined>,
        new Promise<never>((_, rej) => {
          window.setTimeout(() => {
            rej(
              new Error(
                'Нет ответа от MAX за 2 минуты. Закрой диалог, открой мини-приложение снова и повтори.',
              ),
            )
          }, timeoutMs)
        }),
      ])
      const phone = (data?.phone ?? data?.phone_number ?? '').trim()
      if (!phone) {
        throw new Error('Номер не получен или доступ отменён в MAX.')
      }
      return { phone }
    }

    return new Promise((resolve, reject) => {
      const tid = window.setTimeout(() => {
        reject(
          new Error(
            'Нет ответа от MAX за 2 минуты. Закрой диалог, открой мини-приложение снова и повтори.',
          ),
        )
      }, 120_000)
      try {
        fn((contact: { phone_number?: string; phone?: string } | null | undefined) => {
          window.clearTimeout(tid)
          const phone = (contact?.phone ?? contact?.phone_number ?? '').trim()
          if (!phone) {
            reject(new Error('Номер не получен или доступ отменён в MAX.'))
            return
          }
          resolve({ phone })
        })
      } catch (e) {
        window.clearTimeout(tid)
        reject(e instanceof Error ? e : new Error(String(e)))
      }
    })
  },

  haptic(type: 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'selection'): void {
    const webapp = getWebApp()
    if (!webapp || !webapp.hapticFeedback) {
      return
    }
    const typeMap: Record<string, string> = {
      light: 'light',
      medium: 'medium',
      heavy: 'heavy',
      success: 'notificationSuccess',
      error: 'notificationError',
      selection: 'selectionChange',
    }
    webapp.hapticFeedback(typeMap[type])
  },

  backButton: {
    show(): void {
      const webapp = getWebApp()
      if (webapp && webapp.showBackButton) {
        webapp.showBackButton()
      }
    },

    hide(): void {
      const webapp = getWebApp()
      if (webapp && webapp.hideBackButton) {
        webapp.hideBackButton()
      }
    },

    onClick(cb: () => void): void {
      const webapp = getWebApp()
      if (webapp && webapp.setBackButtonCallback) {
        webapp.setBackButtonCallback(cb)
      }
    },

    offClick(cb: () => void): void {
      const webapp = getWebApp()
      if (webapp && webapp.offBackButtonPressed) {
        webapp.offBackButtonPressed(cb)
      }
    },
  },

  openMaxLink(url: string): void {
    const webapp = getWebApp()
    if (webapp && webapp.openLink) {
      webapp.openLink(url)
    }
  },

  openLink(url: string): void {
    if (typeof window !== 'undefined') {
      window.open(url, '_blank')
    }
  },

  async share(params: {
    text?: string
    link?: string
  }): Promise<'shared' | 'cancelled'> {
    return new Promise((resolve) => {
      const webapp = getWebApp()
      if (!webapp || !webapp.shareToStory) {
        resolve('cancelled')
        return
      }

      const url = params.link || window.location.href
      webapp.shareToStory(url, (success) => {
        resolve(success ? 'shared' : 'cancelled')
      })
    })
  },

  closingConfirmation: {
    enable(): void {
      const webapp = getWebApp()
      if (webapp && webapp.enableClosingConfirmation) {
        webapp.enableClosingConfirmation()
      }
    },

    disable(): void {
      const webapp = getWebApp()
      if (webapp && webapp.disableClosingConfirmation) {
        webapp.disableClosingConfirmation()
      }
    },
  },

  get platform(): 'ios' | 'android' | 'desktop' | 'web' {
    const webapp = getWebApp()
    return (webapp && (webapp.platform as any)) || 'web'
  },

  get version(): string {
    const webapp = getWebApp()
    return (webapp && webapp.version) || '0.0.0'
  },
}
